import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { UnitLabel } from '@/shared/config/config'
import { Button, Card, CardBody, CardHeader, CardTitle, DataBlock } from '@/common'
import Drawer from '@/shared/components/ui/Drawer'
import FlueGasCleaningEmissionForm, {
  CarbonateDirectEmission
} from './FlueGasCleaningEmissionsForm'
import { Artifacts } from '@/api/EmissionReportModel'
import { usePermissions } from '@/shared/hooks/usePermissions'
import z from 'zod'

const schema = z.object({
  activityData: z.string().min(1),
  emissionFactor: z.string().min(1),
  conversionFactor: z.string().min(1),
  carbonate: z.string().min(1),
  isOutputBased: z.boolean(),
  materialName: z.string().min(1)
})

export default function FlueGasCleaningCalculatorPanel({
  isOpen,
  close,
  onSubmit,
  carbonate,
  initialValues
}: {
  isOpen: boolean
  close: () => void
  onSubmit: (emission: number, artifacts: Artifacts) => void
  carbonate?: string
  initialValues: Artifacts
}) {
  const { t } = useTranslation()
  const [emission, setEmission] = useState<CarbonateDirectEmission | null>(null)
  const { checkPermission } = usePermissions()
  const canEditReport = checkPermission('emissionReport', 'update')
  const isValid = emission !== null && emission.emission >= 0
  const onAddClick = useCallback(() => {
    if (!isValid) {
      return
    }
    const artifacts = emission
    onSubmit(emission.emission, artifacts)
    close()
  }, [close, emission, onSubmit, isValid])

  const validation = schema.safeParse(emission)

  const isSaveDisabled = !validation.success || !canEditReport || !isValid

  return (
    <Drawer isOpen={isOpen} close={close}>
      <Card>
        <CardHeader>
          <CardTitle title={t('report:calculators.flueGasCleaningCalculatorPanel.title')} />
        </CardHeader>
        <CardBody className="space-y-4 p-4">
          <FlueGasCleaningEmissionForm
            onChange={setEmission}
            addEmission={setEmission}
            allowOutputBasedCalcium={true}
            showCarbonate
            initialValues={
              initialValues || {
                emission: '',
                activityData: '',
                emissionFactor: '',
                conversionFactor: '1',
                carbonate: carbonate || '',
                gypsumProducedTonnes: '',
                isOutputBased: false
              }
            }
          />
          <DataBlock
            data={`${emission?.emission || 0} ${UnitLabel.tCO2}`}
            label={t('report:common:emission')}
          />
        </CardBody>
        <footer className="grid grid-cols-2 gap-4 pb-4 px-4">
          <Button onClick={close} variant="subtle">
            {t('common:actions.cancel')}
          </Button>
          <Button disabled={isSaveDisabled} onClick={onAddClick}>
            {t('common:actions.add')}
          </Button>
        </footer>
      </Card>
    </Drawer>
  )
}
